// @flow
import React, { type Node } from 'react';
import { Tracking, TrackingProvider } from '@expert360/tracking';
import { type UserContextType } from 'core/context/user';
import FeedbackContext, {
  type RenderFeedbackType,
} from 'core/context/feedback';
import RelayEnvironmentContext from 'core/context/relayEnvironment';
import { type RelayEnvironmentType } from 'core/withRelayEnvironment';
import {
  FeatureFlagContainer,
  ConfigProvider,
  FeatureFlagProvider,
} from 'utils';

export type ConfigProps = {
  csrfToken: string,
  SEGMENT_API_KEY?: string,
};
export type UserProps = UserContextType;
export type FlagProps = {
  [flagName: string]: boolean,
};

type ProviderProps = {
  children: Node,
  config: ConfigProps,
  user: UserContextType,
  flags: FlagProps,
  renderFeedback: RenderFeedbackType,
  relayEnvironment: RelayEnvironmentType,
};

const Providers = ({
  children,
  config,
  user,
  flags,
  renderFeedback,
  relayEnvironment,
}: ProviderProps) => {
  const trackingInstance = new Tracking({
    segment: {
      apiKey: config.SEGMENT_API_KEY,
      options: {
        integrations: {
          FullStory: /^(?!\/admin)/i.test(window.location.pathname),
        },
      },
    },
    isImpersonating: user?.isImpersonating ?? false,
    user: user || {},
  });

  const featureFlagContainer = new FeatureFlagContainer(flags);

  return (
    <TrackingProvider instance={trackingInstance}>
      <FeatureFlagProvider.Provider value={featureFlagContainer}>
        <ConfigProvider config={config}>
          <FeedbackContext.Provider value={renderFeedback}>
            <RelayEnvironmentContext.Provider
              value={{
                relayEnvironment,
              }}
            >
              {children}
            </RelayEnvironmentContext.Provider>
          </FeedbackContext.Provider>
        </ConfigProvider>
      </FeatureFlagProvider.Provider>
    </TrackingProvider>
  );
};

export default Providers;
